import React, { useState, useEffect, useRef } from 'react';
import {
    FiMapPin,
    FiNavigation,
    FiClock,
    FiPhone,
    FiCompass,
    FiExternalLink,
    FiInfo
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import {
    BUSINESS_WHATSAPP_CHAT_URL,
    BUSINESS_PHONE_MAIN,
    BUSINESS_HOURS
} from '../../config/businessInfo';
import storeLocationService from '../../services/storeLocationService';
import { formatLocationItem } from '../../utils/locationUtils';
import './ShowroomMapSection.css';

const ShowroomMapSection = ({
    title = 'Visit Our Flagship Store & Warehouse Locations',
    subtitle = 'Experience genuine appliances in person, consult with appliance specialists, and inspect box-packed stock directly at our verified locations.'
}) => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [shouldLoadMap, setShouldLoadMap] = useState(false);
    const mapWrapperRef = useRef(null);

    // Fetch dynamic store and warehouse locations on mount
    useEffect(() => {
        let isMounted = true;

        const fetchLocations = async () => {
            try {
                setLoading(true);
                setError(null);
                const rawList = await storeLocationService.getAll({ active_only: true });

                if (!isMounted) return;

                if (Array.isArray(rawList) && rawList.length > 0) {
                    const normalized = rawList
                        .map((item, idx) => formatLocationItem(item, idx))
                        .filter((item) => item && item.isActive)
                        .sort((a, b) => a.displayOrder - b.displayOrder);

                    setLocations(normalized);
                } else {
                    setLocations([]);
                }
            } catch (err) {
                if (!isMounted) return;
                console.warn(
                    '[ShowroomMapSection] Failed to load store locations from backend:',
                    err?.message || err
                );
                setError(err);
                setLocations([]);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchLocations();

        return () => {
            isMounted = false;
        };
    }, []);

    // Ensure selectedIndex stays in bounds if list changes
    useEffect(() => {
        if (selectedIndex >= locations.length && locations.length > 0) {
            setSelectedIndex(0);
        }
    }, [locations.length, selectedIndex]);

    // Lazy load map when section scrolls into viewport
    useEffect(() => {
        if (!mapWrapperRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setShouldLoadMap(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px' }
        );
        observer.observe(mapWrapperRef.current);
        return () => observer.disconnect();
    }, [loading, locations.length]);

    // Skeleton loader while fetching initial data
    if (loading && locations.length === 0) {
        return (
            <section className="showroom-map-section" aria-label="Loading Store & Warehouse Locations">
                <div className="container">
                    <div className="showroom-map-header">
                        <span className="section-eyebrow">PHYSICAL STORE &amp; WAREHOUSE</span>
                        <h2 className="showroom-map-title">{title}</h2>
                        <p className="showroom-map-subtitle">{subtitle}</p>
                        <div className="showroom-header-accent-line" />
                    </div>

                    <div className="showroom-map-master-card showroom-master-skeleton">
                        <div className="showroom-map-embed-wrapper showroom-skeleton-box">
                            <div className="showroom-skeleton-shimmer" />
                        </div>
                        <div className="showroom-map-details-col">
                            <div className="showroom-info-compact-header">
                                <div className="showroom-skeleton-line showroom-skeleton-line--pill" />
                                <div className="showroom-skeleton-line showroom-skeleton-line--pill" />
                            </div>
                            <div className="showroom-skeleton-line showroom-skeleton-line--title" />
                            <div className="showroom-contact-grid">
                                <div className="showroom-skeleton-line" style={{ width: '90%' }} />
                                <div className="showroom-skeleton-line" style={{ width: '70%' }} />
                                <div className="showroom-skeleton-line" style={{ width: '80%' }} />
                            </div>
                            <div className="showroom-cta-action-row">
                                <div className="showroom-skeleton-line showroom-skeleton-line--btn" />
                                <div className="showroom-skeleton-line showroom-skeleton-line--btn" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // If API returned 0 active locations, gracefully do not render the section
    if (!loading && locations.length === 0) {
        return null;
    }

    const activeLocation = locations[selectedIndex] || locations[0];

    // Build personalized WhatsApp inquiry message for current active location
    const baseWa = (BUSINESS_WHATSAPP_CHAT_URL || '').split('?')[0] || 'https://wa.me/923004456555';
    const waText = encodeURIComponent(
        `Assalam-o-Alaikum, I am inquiring about visiting your ${activeLocation.name} (${activeLocation.fullAddressDisplay}). Please share exact directions and showroom assistance.`
    );
    const whatsappUrl = `${baseWa}?text=${waText}`;

    return (
        <section className="showroom-map-section" aria-label="Showroom Location & Directions">
            <div className="container">
                <div className="showroom-map-header">
                    <span className="section-eyebrow">PHYSICAL STORE &amp; WAREHOUSE</span>
                    <h2 className="showroom-map-title">{title}</h2>
                    <p className="showroom-map-subtitle">{subtitle}</p>
                    <div className="showroom-header-accent-line" />
                </div>

                {/* Multi-Location Tab Switcher (Visible when admin configures more than 1 location) */}
                {locations.length > 1 && (
                    <div
                        className="showroom-locations-tabs-bar"
                        role="tablist"
                        aria-label="Store and warehouse location tabs"
                    >
                        {locations.map((loc, idx) => (
                            <button
                                key={loc.id}
                                type="button"
                                role="tab"
                                aria-selected={selectedIndex === idx}
                                className={`showroom-location-tab-btn ${selectedIndex === idx ? 'is-active' : ''}`}
                                onClick={() => {
                                    setSelectedIndex(idx);
                                    setShouldLoadMap(true);
                                }}
                            >
                                <span className="location-tab-type-tag">
                                    {loc.typeLabel}
                                </span>
                                <span className="location-tab-name">
                                    {loc.name}
                                </span>
                                {loc.city && (
                                    <span className="location-tab-city">
                                        ({loc.city})
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                <div className="showroom-map-master-card">
                    {/* Left: Clean, Unobstructed Live Google Map (Dynamic from Admin Data) */}
                    <div className="showroom-map-embed-wrapper" ref={mapWrapperRef}>
                        {shouldLoadMap ? (
                            <iframe
                                key={activeLocation.id}
                                title={`${activeLocation.name} Google Map Location`}
                                src={activeLocation.embedMapUrl}
                                className="showroom-iframe"
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        ) : (
                            <div
                                className="showroom-map-placeholder-fast"
                                onClick={() => setShouldLoadMap(true)}
                                role="button"
                                tabIndex={0}
                                aria-label="Load live interactive map"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setShouldLoadMap(true);
                                    }
                                }}
                            >
                                <div className="showroom-map-placeholder-content">
                                    <div className="placeholder-pin-pulse">
                                        <FiMapPin />
                                    </div>
                                    <p className="placeholder-text-bold">
                                        {activeLocation.name}
                                    </p>
                                    <p className="placeholder-text-sub">
                                        {activeLocation.fullAddressDisplay} • Tap to view live map
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-outline placeholder-load-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShouldLoadMap(true);
                                        }}
                                    >
                                        <FiCompass /> Load Interactive Map
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Desktop-only floating glass card (hidden on mobile to keep map 100% visible) */}
                        <div className="map-floating-overlay-card map-floating-overlay-card--desktop">
                            <div className="overlay-pin-icon">
                                <FiMapPin />
                            </div>
                            <div className="overlay-text">
                                <strong>{activeLocation.name}</strong>
                                <span>{activeLocation.fullAddressDisplay}</span>
                            </div>
                            <a
                                href={activeLocation.googleMapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="overlay-nav-btn"
                                title="Open in Google Maps App"
                            >
                                <FiNavigation /> Open Map
                            </a>
                        </div>
                    </div>

                    {/* Right: Dynamic Information Panel configured by admin */}
                    <div className="showroom-map-details-col">
                        <div className="showroom-info-compact-header">
                            <span className="showroom-live-pill">
                                <FiMapPin className="showroom-pill-check" /> {activeLocation.typeLabel}
                            </span>
                            <span className="showroom-open-badge">
                                <span className="live-indicator-dot" />{' '}
                                {activeLocation.openingHours || (BUSINESS_HOURS?.lines ? BUSINESS_HOURS.lines[0] : 'Open Mon to Sat')}
                            </span>
                        </div>

                        {/* Location Name */}
                        <h3 className="showroom-location-main-name">
                            {activeLocation.name}
                        </h3>

                        {/* Essential Details Grid */}
                        <div className="showroom-contact-grid">
                            {/* Complete Address */}
                            <div className="showroom-detail-row">
                                <div className="detail-icon-box">
                                    <FiMapPin />
                                </div>
                                <div className="detail-content">
                                    <span className="detail-label">Address</span>
                                    <p className="detail-value">{activeLocation.fullAddressDisplay}</p>
                                    {(activeLocation.area || activeLocation.city) && (
                                        <span className="detail-hint">
                                            {[activeLocation.area, activeLocation.city].filter(Boolean).join(' • ')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Opening & Closing Hours */}
                            {Boolean(activeLocation.openingHours || BUSINESS_HOURS?.lines?.[0]) && (
                                <div className="showroom-detail-row">
                                    <div className="detail-icon-box">
                                        <FiClock />
                                    </div>
                                    <div className="detail-content">
                                        <span className="detail-label">Timings</span>
                                        <p className="detail-value">
                                            {activeLocation.openingHours || BUSINESS_HOURS.lines[0]}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Contact Number(s) */}
                            <div className="showroom-detail-row">
                                <div className="detail-icon-box">
                                    <FiPhone />
                                </div>
                                <div className="detail-content">
                                    <span className="detail-label">Helpline &amp; Inquiries</span>
                                    <div className="detail-phones">
                                        {activeLocation.contactNumbers.length > 0 ? (
                                            activeLocation.contactNumbers.map((p, idx) => (
                                                <a
                                                    key={`${p.tel}-${idx}`}
                                                    href={`tel:${p.tel}`}
                                                    className="phone-badge-link"
                                                    title={`Call ${p.display}`}
                                                >
                                                    {p.display}
                                                </a>
                                            ))
                                        ) : BUSINESS_PHONE_MAIN?.length > 0 ? (
                                            BUSINESS_PHONE_MAIN.map((p) => (
                                                <a
                                                    key={p.tel}
                                                    href={`tel:${p.tel}`}
                                                    className="phone-badge-link"
                                                >
                                                    {p.display}
                                                </a>
                                            ))
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* Optional Description / Additional Info */}
                            {Boolean(activeLocation.description) && (
                                <div className="showroom-detail-row">
                                    <div className="detail-icon-box">
                                        <FiInfo />
                                    </div>
                                    <div className="detail-content">
                                        <span className="detail-label">Information</span>
                                        <p className="detail-value" style={{ fontWeight: 500, fontSize: '0.88rem', color: '#475569' }}>
                                            {activeLocation.description}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="showroom-cta-action-row">
                            <a
                                href={activeLocation.googleMapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-map-primary"
                                title={`Get live directions to ${activeLocation.name} on Google Maps`}
                            >
                                <FiCompass /> Get Directions on Google Maps <FiExternalLink />
                            </a>
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-map-whatsapp"
                                title={`Inquire on WhatsApp about ${activeLocation.name}`}
                            >
                                <FaWhatsapp /> WhatsApp Location
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ShowroomMapSection;
